import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { api } from '../lib/api';

const PINGate = ({ children, onVerified, module = 'hr' }) => {
  const [pinStatus, setPinStatus] = useState(null); // null = loading, 'needs_pin' = needs verification, 'verified' = access granted
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkPinStatus();
  }, [module]);

  const checkPinStatus = async () => {
    try {
      const response = await api.getHRFinancePinStatus(module);
      if (response.data?.has_session) {
        setPinStatus('verified');
        if (onVerified) onVerified();
      } else {
        setPinStatus('needs_pin');
      }
    } catch (error) {
      console.error('Error checking PIN status:', error);
      setPinStatus('needs_pin');
    }
  };

  const handleVerifyPin = async (e) => {
    e.preventDefault();
    
    if (!pin) {
      toast.error('Please enter the PIN');
      return;
    }
    
    setLoading(true);
    try {
      await api.verifyHRFinancePin(pin, module);
      toast.success('PIN verified');
      setPinStatus('verified');
      setPin('');
      if (onVerified) onVerified();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid PIN');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (pinStatus === null) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Verified - show children
  if (pinStatus === 'verified') {
    return children;
  }

  // Module display info
  const moduleInfo = {
    hr: {
      title: 'HR Module',
      color: 'indigo',
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-600'
    },
    finance: {
      title: 'Finance Module',
      color: 'amber',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-600'
    }
  };

  const info = moduleInfo[module] || moduleInfo.hr;

  // PIN Verification UI
  return (
    <div className="flex items-center justify-center min-h-[500px] bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-xl border border-slate-200">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 ${info.bgColor} rounded-full mb-4`}>
            <Shield className={`w-8 h-8 ${info.textColor}`} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">
            Enter {info.title} PIN
          </h2>
          <p className="text-slate-500 mt-2">
            Enter the security PIN to access {info.title}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleVerifyPin} className="space-y-6">
          {/* PIN Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Security PIN
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type={showPin ? 'text' : 'password'}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••••"
                className="pl-10 pr-10 text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                data-testid="pin-input"
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className={`w-full ${module === 'finance' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white py-6 text-lg`}
            data-testid="pin-submit-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Verifying...
              </span>
            ) : (
              'Verify & Access'
            )}
          </Button>
        </form>

        {/* Security Note */}
        <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-800">
            <strong>Security Note:</strong> Your PIN session expires after 30 minutes of inactivity. 
            You'll need to re-enter the PIN to continue accessing {info.title} data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PINGate;
